/**
 * The one small chart on a dashboard card: a few bars, or one whole cut into
 * parts. Home-made, like the board's other small charts (`reports/Sparkline`),
 * so it reads as part of the same app rather than a charting library's.
 */
import type { BoardroomGlanceChart } from '../../../types';

const fmt = (n: number) => n.toLocaleString();

// The split's parts, in order: the first is what is done, the rest what is not.
const PART_TONES = [
  'bg-indigo-500 dark:bg-indigo-400',
  'bg-amber-400 dark:bg-amber-500',
  'bg-gray-300 dark:bg-gray-600',
];

export default function GlanceChart({ chart }: { chart: BoardroomGlanceChart }) {
  if (chart.type === 'bars') {
    const top = chart.max ?? Math.max(1, ...chart.bars.map(b => b.value));
    return (
      <div className="space-y-1" data-testid="glance-bars">
        {chart.bars.map(b => (
          <div key={b.label} className="flex items-center gap-2 text-[11px]">
            <span className="w-24 shrink-0 truncate text-gray-600 dark:text-gray-300" title={b.label}>
              {b.label}
            </span>
            <span className="relative h-2 min-w-0 flex-1 rounded-sm bg-gray-100 dark:bg-gray-700">
              <span
                className="absolute inset-y-0 left-0 rounded-sm bg-indigo-500 dark:bg-indigo-400"
                style={{ width: `${Math.min(100, (b.value / top) * 100)}%` }}
              />
            </span>
            <span className="w-10 shrink-0 text-right tabular-nums text-gray-500 dark:text-gray-400">
              {fmt(b.value)}
              {chart.unit === '%' || chart.unit?.startsWith('%') ? '%' : ''}
            </span>
          </div>
        ))}
        {chart.unit && !chart.unit.startsWith('%') && (
          <div className="text-[10px] text-gray-400 dark:text-gray-500">{chart.unit}</div>
        )}
      </div>
    );
  }
  const total = chart.parts.reduce((n, p) => n + p.value, 0) || 1;
  const pct = chart.unit === '%';
  return (
    <div data-testid="glance-split">
      <div className="flex h-2.5 overflow-hidden rounded-sm bg-gray-100 dark:bg-gray-700">
        {chart.parts.map((p, i) => (
          <span
            key={p.label}
            className={PART_TONES[i % PART_TONES.length]}
            style={{ width: `${(p.value / total) * 100}%` }}
            title={`${p.label}: ${fmt(p.value)}${pct ? '%' : ''}`}
          />
        ))}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600 dark:text-gray-300">
        {chart.parts.map((p, i) => (
          <span key={p.label} className="inline-flex items-center gap-1">
            <span className={`inline-block h-2 w-2 rounded-sm ${PART_TONES[i % PART_TONES.length]}`} aria-hidden />
            {p.label} <span className="tabular-nums text-gray-500 dark:text-gray-400">{fmt(p.value)}{pct ? '%' : ''}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
